import React, { ReactNode, useContext } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { AccessContext } from "@me-team/host/src/components/App/App";
import { CompanyAccess, PermissionScope } from "@me-team/host/main/graphql/types";
import AccessService from "@me-team/host/services/AccessService";
import { AllModulesTagName } from "../utils/enums";
import {
    hasRoutePermissionAccess,
    hasRoutePermissionConfig,
    isRoutePermissionAccessPending,
} from "./routePermissionAccess";

export interface RouteAccessProps {
    requiredModules?: AllModulesTagName[];
    permission?: CompanyAccess;
    anyPermissions?: CompanyAccess[];
    allPermissions?: CompanyAccess[];
    scope?: PermissionScope;
    pendingFallback?: ReactNode;
    forbiddenFallback?: ReactNode;
    redirectTo?: string;
}

interface ProtectedRouteProps extends RouteAccessProps {
    children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    requiredModules = [],
    permission,
    anyPermissions,
    allPermissions,
    scope,
    pendingFallback = null,
    forbiddenFallback,
    redirectTo = "/error/403",
}) => {
    const { isReady } = useContext(AccessContext);
    const companyId = useReactiveVar(currentCompanyIdVar);
    const { currentCompanyId } = useParams();
    const accessServiceInstance = AccessService.getInstance();

    const isInvalidCompanyId = companyId && +currentCompanyId !== +companyId;
    const hasPermissionConfig = hasRoutePermissionConfig({ permission, anyPermissions, allPermissions });
    const isPermissionAccessPending = isRoutePermissionAccessPending(
        accessServiceInstance,
        isReady,
        hasPermissionConfig
    );
    const hasNoModuleAccess = requiredModules.length > 0 && !accessServiceInstance.areModulesActive(requiredModules);
    const hasNoPermissionAccess =
        hasPermissionConfig &&
        !hasRoutePermissionAccess(accessServiceInstance, {
            permission,
            anyPermissions,
            allPermissions,
            scope,
        });

    if (isInvalidCompanyId) {
        return forbiddenFallback !== undefined ? <>{forbiddenFallback}</> : <Navigate to={redirectTo} replace />;
    }

    if (isPermissionAccessPending) {
        return <>{pendingFallback}</>;
    }

    if (hasNoModuleAccess || hasNoPermissionAccess) {
        return forbiddenFallback !== undefined ? <>{forbiddenFallback}</> : <Navigate to={redirectTo} replace />;
    }
    return children;
};

export default ProtectedRoute;
